import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BOTTOM_BANNER_ENABLED, FREESTAR_ENABLED } from 'src/common/constants/experiments/ads';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import { useMediaQuery } from 'src/common/hooks/useMediaQuery';
import { useRouteProps } from 'src/common/providers/RoutePropsProvider';
import { selectBottomAdBannerHeight, selectIsMobile } from 'src/common/selectors/app';
import { selectIsDesktopNowPlayingEnabled } from 'src/common/selectors/config';
import { getDisplayAdAttributes, gptSlotNames } from 'src/common/utils/ads/config';
import { useDetermineAdEligibility } from 'src/common/utils/ads/determineAdEligibility';
import { useShouldShowPlayer } from 'src/common/utils/playerStatus/shouldShowPlayer';
import { setBottomAdBannerHeight } from '../../actions/app';
import styles from './bottom-banner.module.scss';
import DisplayAd from './DisplayAd';

/**
 * Both banner placements are trafficked `[[728, 90], [320, 50]]`, but only one of those fits at a
 * given breakpoint. The ads config cannot know which — it describes what is sellable, not what is
 * on screen — so the choice is made here and passed down.
 *
 * Mobile user agents get the 320x50 only below this width. Above it (tablets in landscape) a 320x50
 * leaves most of the banner empty, and Ad Ops asked that wide screens never request it.
 *
 * Module constants because the library treats `sizes` as an effect dependency: a fresh array each
 * render would tear down and re-create the slot.
 */
const DESKTOP_BANNER_SIZES = [[728, 90]];
const MOBILE_BANNER_SIZES = [[320, 50]];
const MOBILE_BANNER_MAX_WIDTH_QUERY = '(max-width: 999px)';

export default function BottomBanner({ isNowPlayingBanner }) {
  const routeProps = useRouteProps();
  const canShowAds = useDetermineAdEligibility(routeProps.guideContext?.guideId);

  const bannerRef = useRef();
  const actions = useActions({ setBottomAdBannerHeight });

  const isPlayerOpen = useShouldShowPlayer();
  const bottomAdBannerHeight = useSelector(selectBottomAdBannerHeight);
  const isMobile = useSelector(selectIsMobile);
  // Before hydration, assume a mobile user agent means a narrow screen.
  const isNarrowViewport = useMediaQuery(MOBILE_BANNER_MAX_WIDTH_QUERY, isMobile);
  const isMobileBanner = isMobile && isNarrowViewport;
  const isDesktopNowPlayingEnabled = useSelector(selectIsDesktopNowPlayingEnabled);
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isBottomBannerEnabled = useExperiment(BOTTOM_BANNER_ENABLED);
  const isNowPlayingBannerEnabled = useExperiment('ads.display.nowPlayingBottomBanner.enabled');

  const showDisplayAd =
    canShowAds &&
    !routeProps.hideAds &&
    isBottomBannerEnabled &&
    (!isNowPlayingBanner || isNowPlayingBannerEnabled);

  useEffect(() => {
    if (showDisplayAd) {
      const resizeObserver = new ResizeObserver(() => {
        if (bannerRef.current) {
          const height = bannerRef.current.offsetHeight || 0;
          actions.setBottomAdBannerHeight(height);
        }
      });

      if (bannerRef.current) {
        resizeObserver.observe(bannerRef.current);
      }

      return () => {
        actions.setBottomAdBannerHeight(0);
        resizeObserver.disconnect();
      };
    }
  }, [actions, showDisplayAd]);

  return !showDisplayAd ? null : (
    <div
      ref={bannerRef}
      className={classNames(styles.container, {
        [styles.withPlayer]: (isDesktopNowPlayingEnabled || !isNowPlayingBanner) && isPlayerOpen,
        // This is to prevent rendering bottom border when no content rendered yet
        [styles.withBorder]: (isNowPlayingBanner || isPlayerOpen) && bottomAdBannerHeight,
        [styles.mobile]: isMobileBanner,
      })}
    >
      <DisplayAd
        guideId={routeProps.guideContext?.guideId}
        matchUrl={routeProps.matchUrl}
        sizes={isMobileBanner ? MOBILE_BANNER_SIZES : DESKTOP_BANNER_SIZES}
        {...getDisplayAdAttributes(
          isNowPlayingBanner ? gptSlotNames.nowPlaying_bottom_banner : gptSlotNames.bottom_banner,
          isFreestarEnabled,
        )}
      />
    </div>
  );
}

BottomBanner.propTypes = {
  isNowPlayingBanner: PropTypes.bool,
};
